import { generateUniqueCode, ignoreFalsy, isLoaded, LoadableStatus, TrackService } from '@aiwyn/shared';
import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, from, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { EngagementMode, NON_INTERACTIVE_ENGAGEMENT_STATUSES } from '../../../../api/models';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../../../../routing-constants';
import { EngagementErrorDialogComponent } from '../../components';
import { EngagementLandingComponent } from '../../components/engagement-landing/engagement-landing.component';
import { DestinationEngagementStepRef, EngagementNavigationErrorStrategyModel } from '../../models';
import { EngagementTrackingService } from '../../services/engagement-tracking.service';
import { EngagementsState, GetEngagementHub, MoveToEngagementStep, SetSelectedEngagementId } from '../../state';
import { WebViewerPreloadService } from '../../../../shared/services/webviewer';
import { ViewEngagementContainerComponent } from '../view-engagement-container/view-engagement-container.component';

export enum EngagementHubView {
  LANDING = 'landing',
  DETAIL = 'detail',
  REASSIGNMENT = 'reassignment',
}

@Component({
  selector: 'app-engagement-hub-container',
  templateUrl: './engagement-hub-container.component.html',
  styleUrls: ['./engagement-hub-container.component.scss'],
  standalone: true,
  imports: [  
    CommonModule,
    EngagementLandingComponent,
    ViewEngagementContainerComponent,
  ],
})
export class EngagementHubContainerComponent implements OnInit, OnDestroy {
  private readonly _webViewerPreloadService = inject(WebViewerPreloadService);
  private readonly _engagementTrackingService = inject(EngagementTrackingService);
  private readonly _trackService = inject(TrackService);

  readonly initialView = input<EngagementHubView>();
  readonly isUsingSecureLink = input<boolean>();
  readonly onGoToStep = input<(destination?: DestinationEngagementStepRef) => void>();
  readonly onNavigateToNextEntity = input<() => void>();
  readonly onNavigateToPreviousEntity = input<() => void>();
  readonly showSideDrawer = input<boolean>();
  
  readonly engagementHubView = output<EngagementHubView>();
  readonly sideDrawerOpened = output<boolean>();
  
  readonly view$: Observable<EngagementHubView>;
  readonly EngagementHubView = EngagementHubView;
  readonly detailInitialized = signal(false);
  private readonly _engagementId = signal(0);
  private readonly _viewSubject: BehaviorSubject<EngagementHubView>;
  private readonly _unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store,
    private readonly dialog: MatDialog,
  ) {
    this._viewSubject = new BehaviorSubject<EngagementHubView>(EngagementHubView.LANDING);
    this.view$ = combineLatest([
      this._viewSubject.asObservable(),
      this.store.select(EngagementsState.getSelectedEngagement).pipe(ignoreFalsy()),
    ]).pipe(
      map(([currentView, engagement]) => engagement.mode === EngagementMode.MANUAL ? EngagementHubView.DETAIL : currentView),
      tap((view) => {
        if (view === EngagementHubView.DETAIL) {
          this.detailInitialized.set(true);
        }
        
        this.engagementHubView.emit(view);
      }),
    );

    effect(() => {
      const initialViewValue = this.initialView();

      if (initialViewValue) {
        const currentValue = this._viewSubject.value;

        // Allow transition from LANDING (initial load) or to REASSIGNMENT (user action)
        if (currentValue === EngagementHubView.LANDING || initialViewValue === EngagementHubView.REASSIGNMENT) {
          this._viewSubject.next(initialViewValue);
        }
      }
    });
  }

  ngOnInit(): void {
    // Secure link is handled by the engagement wrapper component
    if (this.isUsingSecureLink()) {
      this._webViewerPreloadService.preload();
      return;
    }

    this.activatedRoute.params.pipe(
      switchMap(params => {
        const engagementIdParam = params?.['engagementId'];

        if (!engagementIdParam || isNaN(+engagementIdParam)) {
          return from(this.router.navigateByUrl(`${ROUTE_TOKEN_MAIN_BASE}/${ROUTE_TOKEN_ENGAGEMENTS_BASE}`));
        }

        this._engagementId.set(+engagementIdParam);

        this._engagementTrackingService.trackEngagementOpened(this._engagementId());

        // Skip the hub call and WebViewer preload if the engagement is non-interactive
        const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);

        if (selectedEngagement?.id === this._engagementId() && NON_INTERACTIVE_ENGAGEMENT_STATUSES.includes(selectedEngagement.status)) {
          return this.store.dispatch(new SetSelectedEngagementId(this._engagementId()));
        }

        this._webViewerPreloadService.preload();

        return this.store.dispatch([
          new SetSelectedEngagementId(this._engagementId()),
          new GetEngagementHub(this._engagementId()),
        ]);
      }),
      takeUntil(this._unsubscribe),
    ).subscribe();

    this.store.select(EngagementsState.getSelectedEngagementReportStatus).pipe(
      isLoaded(true),
      take(1),
      filter(status => status === LoadableStatus.ERROR),
      tap(() => this.openNavigationErrorDialog(this._engagementId())),
      takeUntil(this._unsubscribe),
    ).subscribe();

    this.activatedRoute.queryParams.pipe(
      map(params => params?.['enabledReassignment'] === 'true'),
      filter(enabledReassignment => !!enabledReassignment),
      tap(() => this._viewSubject.next(EngagementHubView.LANDING)),
      takeUntil(this._unsubscribe),
    ).subscribe();
  }

  private openNavigationErrorDialog(engagementId: number): void {
    const errorCode = `ENG${engagementId}-ViewEngagement-${generateUniqueCode()}`;

    this._trackService.track('Navigation Engagement Error :: ViewEngagement failed', { engagementId, errorCode });

    const concreteStrategy = new EngagementNavigationErrorStrategyModel(engagementId, errorCode);

    this.dialog.open(EngagementErrorDialogComponent, {
      data: { concreteStrategy, errorCode },
      disableClose: true,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  onAdvance(destination?: DestinationEngagementStepRef): void {
    // In secure link mode, call onGoToStep if provided to select the correct entity
    if (this.isUsingSecureLink()) {
      const onGoToStep = this.onGoToStep();

      onGoToStep(destination);
      this._viewSubject.next(EngagementHubView.DETAIL);

      // Don't continue with the rest of the initialization as the entity is already selected
      return;
    }
    
    if (destination) {
      const currentStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementCurrentStep);

      const isSameStep = (typeof destination === 'string')
        ? (destination !== 'previous' && destination !== 'next' && destination === currentStep.type)
        : (destination?.type === currentStep.type && (!destination.documentId || currentStep.tasks?.some(task => task.documentId === destination.documentId)));

      if (!isSameStep) {
        this.store.dispatch(new MoveToEngagementStep(destination));
      }
    } else if (!this.detailInitialized()) {
      this.store.dispatch(new MoveToEngagementStep(undefined));
    }

    this._viewSubject.next(EngagementHubView.DETAIL);

    // Clear the enabledReassignment query param so clicking "Reassign" again triggers a fresh navigation
    this.router.navigate([], { queryParams: { enabledReassignment: null }, queryParamsHandling: 'merge' });
  }
}
